'use client';

import { useEffect, useRef, useState } from 'react';

interface Props {
  /** Sanitized rich-text HTML. */
  bodyHtml: string;
}

const TOGGLE_CLASS =
  'text-brand-text mt-1 inline-block text-[14px] font-semibold';

/** Collapsed height cap — the preview shown before "Read more…". */
const COLLAPSED_MAX_H = 288; // px (Tailwind max-h-72)

/**
 * Detail-view rich-text body with an IN-PLACE "Read more…" ⇄ "Read less…"
 * toggle. Used by the post-detail modal + permalink page, where the full body
 * lives on THIS surface — so the CTA expands/collapses locally instead of
 * navigating (a nav from the detail view would loop back onto itself). The feed
 * keeps the server-rendered {@link PostTextBody} path, which links out to open
 * the post instead of toggling.
 *
 * Whether to show the toggle is decided by MEASURING real overflow against the
 * collapsed cap — not by a char-count heuristic. A char count can't know the
 * rendered height (line breaks, embedded media, viewport width), so it produced
 * a toggle that hid nothing for a >500-char but short body. Here the toggle
 * appears only when the content genuinely exceeds the collapsed height.
 */
export function ExpandableTextBody({ bodyHtml }: Props) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // Measure after render (and on resize): the body overflows the collapsed cap
  // when its natural height exceeds COLLAPSED_MAX_H. Re-measures if the HTML
  // changes. `scrollHeight` is the full content height regardless of the clamp.
  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => setOverflows(el.scrollHeight > COLLAPSED_MAX_H + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [bodyHtml]);

  const clipped = overflows && !expanded;

  return (
    <div>
      <div
        ref={bodyRef}
        className="post-html-body text-[14px] leading-relaxed opacity-90"
        style={
          clipped
            ? { maxHeight: COLLAPSED_MAX_H, overflow: 'hidden' }
            : undefined
        }
        dangerouslySetInnerHTML={{ __html: bodyHtml }}
      />
      {overflows ? (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className={TOGGLE_CLASS}
        >
          {expanded ? 'Read less…' : 'Read more…'}
        </button>
      ) : null}
    </div>
  );
}
